import React from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, Col, Row } from "reactstrap";
import type { ServicePerformanceMetrics } from "../../utils/dashboardMetrics";
import { formatDashboardMoney } from "../../utils/dashboardMetrics";

const piechartColors = ["#777aca", "#5156be", "#a8aada", "#34c38f"];

interface WalletBalanceProps {
  services: ServicePerformanceMetrics[];
  currency?: string;
  loading?: boolean;
}

const WalletBalance = ({ services, currency, loading }: WalletBalanceProps) => {
  const series = services.map((s) => Number(s.amount.toFixed(2)));
  const hasData = series.some((v) => v > 0) || services.some((s) => s.count > 0);
  const chartSeries = hasData ? series.map((v, i) => (v > 0 ? v : services[i].count)) : [1];

  const options: Object = {
    chart: {
      width: 227,
      height: 227,
      type: "pie",
    },
    labels: hasData ? services.map((s) => s.label) : ["No data"],
    colors: hasData ? piechartColors : ["#e9e9ef"],
    stroke: {
      width: 0,
    },
    legend: {
      show: false,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
        },
      },
    ],
  };

  const legendColors = ["success", "primary", "info", "warning"];

  return (
    <React.Fragment>
      <Col xl={5}>
        <Card className="card-h-100">
          <CardBody>
            <div className="d-flex flex-wrap align-items-center mb-4">
              <h5 className="card-title me-2">Service Performance</h5>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Row className="align-items-center">
                <div className="col-sm">
                  <div id="wallet-balance" className="apex-charts">
                    <ReactApexChart
                      options={options}
                      series={chartSeries}
                      type="pie"
                      height="227"
                    />
                  </div>
                </div>
                <div className="col-sm align-self-center">
                  <div className="mt-4 mt-sm-0">
                    {services.map((service, index) => (
                      <div key={service.type} className={index === 0 ? "" : "mt-4 pt-2"}>
                        <p className="mb-2">
                          <i
                            className={`mdi mdi-circle align-middle font-size-10 me-2 text-${legendColors[index % legendColors.length]}`}
                          ></i>{" "}
                          {service.label}
                        </p>
                        <h6>
                          {service.count} Bookings ={" "}
                          <span className="text-muted font-size-14 fw-normal">
                            {formatDashboardMoney(service.amount, currency)}
                          </span>
                        </h6>
                      </div>
                    ))}
                    {!hasData && (
                      <p className="text-muted mb-0">No data available for this period</p>
                    )}
                  </div>
                </div>
              </Row>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default WalletBalance;
